import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Users,
  MessageSquare,
  Trash2,
  Calendar,
  Phone,
  Plus,
  Search,
  X,
  Upload,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Layout from "../components/Layout";
import { useTheme } from "../contexts/ThemeContext";
import { groupsAPI, messagesAPI, contactsAPI } from "../services/api";
import api from "../services/api";
import { formatarData, formatarDataHora } from "../utils/dateFormatter";
import { PageSkeleton, SkeletonCard } from '../components/Skeleton';

const GroupDetails = () => {
  const { darkMode } = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [group, setGroup] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showAddModal, setShowAddModal] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [idDelete, setIdDelete] = useState(null);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [consentimentoDeclarado, setConsentimentoDeclarado] = useState(false);
  const [importing, setImporting] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [conversoes, setConversoes] = useState({});

  const handleExpandCampaign = async (campanhaId) => {
    const newId = expandedCampaign === campanhaId ? null : campanhaId;
    setExpandedCampaign(newId);
    if (newId && !conversoes[newId]) {
      try {
        const res = await api.get(`/campanhas/${newId}/conversao`);
        setConversoes(prev => ({ ...prev, [newId]: res.data }));
      } catch (e) {}
    }
  };
  const [contactsPage, setContactsPage] = useState(1);
  const [contactSearchTerm, setContactSearchTerm] = useState("");
  const [contactTempFilter, setContactTempFilter] = useState("all");
  const contactsPerPage = 30;
 
  useEffect(() => {
    loadGroupDetails();
    loadAllContacts();

    // Fechar dropdown ao clicar fora
    const handleClickOutside = (event) => {
      if (!event.target.closest(".contact-autocomplete")) {
        setShowContactDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [id]);

  const loadGroupDetails = async () => {
    try {
      // Buscar dados do grupo
      const groupResponse = await groupsAPI.get(id);
      setGroup(groupResponse.data);

      // Buscar contatos do grupo
      const contactsResponse = await groupsAPI.getContacts(id);
      setContacts(contactsResponse.data || []);

      // Buscar mensagens enviadas para o grupo
      const messagesResponse = await messagesAPI.getByGroup(id);
      setMessages(messagesResponse.data || []);
    } catch (error) {
      toast.error("Erro ao carregar detalhes do grupo");
      navigate("/segmentos");
    } finally {
      setLoading(false);
    }
  };

  const loadAllContacts = async () => {
    try {
      const response = await contactsAPI.search(user.cliente_id);
      setAllContacts(response.data.messages || []);
    } catch (error) {
      console.error("Erro ao carregar contatos:", error);
    }
  };

  const handleDeleteGroup = async () => {
    if (idDelete) {
      try {
        await groupsAPI.removeContact(idDelete);
        toast.success("Contato removido do grupo");
        loadGroupDetails();
        setDeleteModal(false);
      } catch (e) {
        toast.error("Erro ao remover contato");
      }
    }
  };

  const handleRemoveContact = async (id) => {
    setDeleteModal(true);
    setIdDelete(id);
    try {
      setIdDelete(id);
    } catch (error) {
      toast.error("Erro ao remover contato");
    }
  };

  const handleAddContact = async (contactId) => {
    if (!id) {
      toast.error('Nenhum grupo selecionado');
      return;
    }

    try {
      const contactData = {
        grupo_id: id,
        contato_id: contactId,
        cliente_id: user.cliente_id,
        status: true,
      };

      await groupsAPI.addContact([contactData]);
      toast.success("Contato adicionado ao grupo!");
      setContactSearch("");
      setShowContactDropdown(false);
      loadGroupDetails();
    } catch (error) {
      console.error("Erro ao adicionar contato:", error);
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          "Erro ao adicionar contato";
      
      if (error.response?.status === 409) {
        toast.warning(errorMessage, { autoClose: 7000 });
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const getAvailableContacts = () => {
    const groupContactPhones = contacts.map((c) => c.telefone);
    const hasSearch = contactSearch.trim().length > 0;
    
    return allContacts.filter(
      (contact) => {
        const isInGroup = groupContactPhones.includes(contact.telefone);
        const isSelected = selectedContacts.includes(contact.id);
        const matchesSearch = contact.nome?.toLowerCase().includes(contactSearch.toLowerCase()) ||
                             contact.telefone?.includes(contactSearch);
        
        // Se tem busca: mostra todos que correspondem (incluindo os do grupo)
        if (hasSearch) {
          return contact.status === true && matchesSearch;
        }
        
        // Sem busca: mostra apenas os que NÃO estão no grupo E NÃO estão selecionados
        return contact.status === true && !isInGroup && !isSelected && matchesSearch;
      }
    ).map(contact => ({
      ...contact,
      alreadyInGroup: groupContactPhones.includes(contact.telefone)
    }));
  };
  if (loading) {
    return (
      <Layout>
        <PageSkeleton>
          <SkeletonCard linhas={6} />
        </PageSkeleton>
      </Layout>
    );
  }


  if (!group) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className={`text-lg font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
            Grupo não encontrado
          </h3>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/segmentos")}
              className={`p-2 hover:bg-opacity-10 rounded-lg ${
                darkMode ? "text-white hover:bg-white" : "text-gray-600 hover:bg-gray-600"
              }`}
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                {group.nome}
              </h1>
              <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                Criado em{" "}
                {formatarData(group.create_at)}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                group.status
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {group.status ? "Ativo" : "Inativo"}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {Array.isArray(messages) && messages.length > 0 && (
            <>
              <div className={`metric-card ${darkMode ? "bg-zinc-800 border-gray-700" : "bg-white border-gray-200"}`}>
                <div
                  className="metric-card__gradient-bar"
                  style={{
                    background: "linear-gradient(135deg, #76C291 0%, #adcd64 100%)",
                  }}
                />

                <div className="metric-card__content">
                  <div className="metric-card__info">
                    <p className={`metric-card__title ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                      Mensagens Enviadas
                    </p>
                    <h3 className={`metric-card__value ${darkMode ? "text-white" : "text-gray-900"}`}>
                      {messages.length}
                    </h3>
                  </div>

                  <div
                    className="metric-card__icon"
                    style={{ background: "rgba(16, 185, 129, 0.1)" }}
                  >
                    <MessageSquare className="h-8 w-8" style={{ color: "#10B981" }} />
                  </div>
                </div>
              </div>

              <div className={`metric-card ${darkMode ? "bg-zinc-800 border-gray-700" : "bg-white border-gray-200"}`}>
                <div
                  className="metric-card__gradient-bar"
                  style={{
                    background: "linear-gradient(135deg, #76C291 0%, #adcd64 100%)",
                  }}
                />

                <div className="metric-card__content">
              <div className="metric-card__info">
                <p className={`metric-card__title ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                  Última Atividade
                </p>
                <p className={` text-sm metric-card__value ${darkMode ? "text-white" : "text-gray-900"}`}>
                  {Array.isArray(messages) && messages.length > 0
                    ? formatarData(
                        Math.max(...messages.map((m) => new Date(m.created_at).getTime()))
                      )
                    : "Nenhuma"}
                </p>
                <span
                  className={`metric-card__subtitle ${darkMode ? "text-gray-400" : "text-gray-500"}`}
                  style={{ color: darkMode ? undefined : "#76C291" }}
                >
                  {Array.isArray(messages) && messages.length > 0
                    ? `${Math.floor(
                        (Date.now() -
                          Math.max(...messages.map((m) => new Date(m.created_at).getTime()))
                        ) /
                          (1000 * 60 * 60 * 24)
                      )} dias atrás`
                    : "Sem atividades"}
                </span>
              </div>

              <div
                className="metric-card__icon"
                style={{ background: "rgba(16, 185, 129, 0.1)" }}
              >
                <Calendar className="h-8 w-8" style={{ color: "#76C291" }} />
              </div>
            </div>
          </div>
            </>
          )}
        </div>

        {/* Conteúdo */}
        <div className={`shadow rounded-lg ${darkMode ? "bg-zinc-800" : "bg-white"}`}>
          <div className="p-6 space-y-6">
            {/* KPIs no topo */}
            {true && (
              <div className="space-y-6">
                {/* KPIs Principais */}
                <div className="flex flex-wrap gap-4">
                  <div className={`flex-1 min-w-[140px] p-5 rounded-lg ${darkMode ? "bg-gradient-to-br from-blue-900/50 to-blue-800/30 border border-blue-700" : "bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Users className="h-8 w-8 text-blue-500" />
                      <span className={`text-xs font-medium px-2 py-1 rounded ${darkMode ? "bg-blue-900/50 text-blue-300" : "bg-blue-200 text-blue-800"}`}>
                        Base
                      </span>
                    </div>
                    <p className={`text-3xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                      {group?.estatisticas?.total_contatos || 0}
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? "text-blue-300" : "text-blue-700"}`}>
                      Contatos no grupo
                    </p>
                  </div>

                  <div className={`flex-1 min-w-[140px] p-5 rounded-lg ${darkMode ? "bg-gradient-to-br from-green-900/50 to-green-800/30 border border-green-700" : "bg-gradient-to-br from-green-50 to-green-100 border border-green-200"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Users className="h-8 w-8 text-green-500" />
                      <span className={`text-xs font-medium px-2 py-1 rounded ${darkMode ? "bg-green-900/50 text-green-300" : "bg-green-200 text-green-800"}`}>
                        Opt-in
                      </span>
                    </div>
                    <p className={`text-3xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                      {contacts.filter(c => c.opt_in_ativo !== false).length}
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? "text-green-300" : "text-green-700"}`}>
                      Aptos a receber {contacts.filter(c => c.opt_in_ativo === false).length > 0 && `(${contacts.filter(c => c.opt_in_ativo === false).length} sem opt-in)`}
                    </p>
                  </div>

                  <div className={`flex-1 min-w-[140px] p-5 rounded-lg ${darkMode ? "bg-gradient-to-br from-purple-900/50 to-purple-800/30 border border-purple-700" : "bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Calendar className="h-8 w-8 text-purple-500" />
                      <span className={`text-xs font-medium px-2 py-1 rounded ${darkMode ? "bg-purple-900/50 text-purple-300" : "bg-purple-200 text-purple-800"}`}>
                        Campanhas
                      </span>
                    </div>
                    <p className={`text-3xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                      {group?.estatisticas?.total_campanhas || 0}
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? "text-purple-300" : "text-purple-700"}`}>
                      Realizadas
                    </p>
                  </div>

                  {(() => {
                    const campanhas = group?.estatisticas?.campanhas || [];
                    const ultima = campanhas[0];
                    if (!ultima) return null;
                    const taxaLeitura = ultima.total_mensagens > 0 ? Math.round((ultima.mensagens_lidas / ultima.total_mensagens) * 100) : 0;
                    return (
                      <div className={`flex-1 min-w-[140px] p-5 rounded-lg ${darkMode ? "bg-gradient-to-br from-yellow-900/50 to-yellow-800/30 border border-yellow-700" : "bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200"}`}>
                        <div className="flex items-center justify-between mb-2">
                          <MessageSquare className="h-8 w-8 text-yellow-500" />
                          <span className={`text-xs font-medium px-2 py-1 rounded ${darkMode ? "bg-yellow-900/50 text-yellow-300" : "bg-yellow-200 text-yellow-800"}`}>
                            Última Campanha
                          </span>
                        </div>
                        <p className={`text-3xl font-bold ${taxaLeitura > 60 ? "text-green-600" : taxaLeitura > 30 ? "text-yellow-600" : "text-red-600"}`}>
                          {taxaLeitura}%
                        </p>
                        <p className={`text-sm mt-1 ${darkMode ? "text-yellow-300" : "text-yellow-700"}`}>
                          Leitura — {ultima.nome}
                        </p>
                      </div>
                    );
                  })()}

                  {/* Card de Erros */}
                  {contacts.filter(c => !!c.ultimo_erro_envio).length > 0 && (
                    <div className={`flex-1 min-w-[140px] p-5 rounded-lg ${darkMode ? "bg-gradient-to-br from-red-900/50 to-red-800/30 border border-red-700" : "bg-gradient-to-br from-red-50 to-red-100 border border-red-200"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <X className="h-8 w-8 text-red-500" />
                        <span className={`text-xs font-medium px-2 py-1 rounded ${darkMode ? "bg-red-900/50 text-red-300" : "bg-red-200 text-red-800"}`}>
                          Erros
                        </span>
                      </div>
                      <p className={`text-3xl font-bold text-red-600`}>
                        {contacts.filter(c => !!c.ultimo_erro_envio).length}
                      </p>
                      <p className={`text-sm mt-1 ${darkMode ? "text-red-300" : "text-red-700"}`}>
                        Números com falha
                      </p>
                    </div>
                  )}
                </div>

                {/* Campanhas */}
                {group?.estatisticas?.campanhas && group.estatisticas.campanhas.length > 0 && (
                  <div>
                    <h3 className={`text-sm font-semibold mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                      Campanhas ({group.estatisticas.campanhas.length})
                    </h3>
                    <table className={`w-full text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                      <thead>
                        <tr className={`border-b ${darkMode ? "border-zinc-700" : "border-gray-200"}`}>
                          <th className="text-left py-2 font-medium">Nome</th>
                          <th className="text-center py-2 font-medium">Envios</th>
                          <th className="text-center py-2 font-medium">Entrega</th>
                          <th className="text-center py-2 font-medium">Leitura</th>
                          <th className="text-center py-2 font-medium">Erros</th>
                          <th className="text-center py-2 font-medium">Data</th>
                          <th className="text-right py-2 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.estatisticas.campanhas.map((campanha) => {
                          const taxaEntrega = campanha.total_mensagens > 0 ? Math.round((campanha.mensagens_entregues / campanha.total_mensagens) * 100) : 0;
                          const taxaLeitura = campanha.total_mensagens > 0 ? Math.round((campanha.mensagens_lidas / campanha.total_mensagens) * 100) : 0;
                          return (
                            <tr key={campanha.id} className={`border-b cursor-pointer transition-colors ${darkMode ? "border-zinc-800 hover:bg-zinc-700/30" : "border-gray-50 hover:bg-gray-50"}`}
                              onClick={() => handleExpandCampaign(campanha.id)}>
                              <td className={`py-2 font-medium truncate max-w-[150px] ${darkMode ? "text-white" : "text-gray-900"}`}>{campanha.nome}</td>
                              <td className="py-2 text-center">{campanha.total_mensagens}</td>
                              <td className={`py-2 text-center font-medium ${taxaEntrega > 80 ? "text-green-600" : taxaEntrega > 50 ? "text-yellow-600" : "text-red-500"}`}>{taxaEntrega}%</td>
                              <td className={`py-2 text-center font-medium ${taxaLeitura > 60 ? "text-green-600" : taxaLeitura > 30 ? "text-yellow-600" : "text-red-500"}`}>{taxaLeitura}%</td>
                              <td className={`py-2 text-center ${campanha.mensagens_erro > 0 ? "text-red-500" : "text-green-600"}`}>{campanha.mensagens_erro}</td>
                              <td className={`py-2 text-center ${darkMode ? "text-gray-500" : "text-gray-400"}`}>{new Date(campanha.create_at).toLocaleDateString('pt-BR')}</td>
                              <td className="py-2 text-right">
                                <a href={`/marketing/campanhas/${campanha.id}`} target="_blank" rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-nomoz-orange hover:underline font-medium">
                                  Ver →
                                </a>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

              </div>
            )}

            {/* Contatos */}
            {true && (
              <div className="space-y-4">
                {/* Add Contact Section */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAddModal(true)}
                    className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      darkMode ? "bg-zinc-700 text-white hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar
                  </button>
                  <button
                    onClick={() => setShowCSVModal(true)}
                    className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      darkMode ? "bg-zinc-700 text-white hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Importar CSV
                  </button>
                </div>

                {/* Filtros */}
                <div className="flex gap-2 flex-wrap">
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por nome ou telefone..."
                      value={contactSearchTerm}
                      onChange={(e) => { setContactSearchTerm(e.target.value); setContactsPage(1); }}
                      className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${
                        darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                  <select
                    value={contactTempFilter}
                    onChange={(e) => { setContactTempFilter(e.target.value); setContactsPage(1); }}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="all">Todos</option>
                    <option value="optin">✅ Com opt-in</option>
                    <option value="nooptin">⚠️ Sem opt-in</option>
                    <option value="erro">❌ Com erro</option>
                  </select>
                </div>

                {/* Contacts List */}
                {contacts.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className={`mx-auto h-12 w-12 ${darkMode ? "text-gray-400" : "text-gray-400"}`} />
                    <h3 className={`mt-2 text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                      Nenhum contato
                    </h3>
                    <p className={`mt-1 text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                      Use o campo acima para adicionar contatos a este grupo.
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h4 className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                        {(() => {
                          const filtered = contacts.filter(c => {
                            const search = contactSearchTerm.toLowerCase();
                            const matchSearch = !search || c.nome?.toLowerCase().includes(search) || c.telefone?.includes(search);
                            const matchOptIn = contactTempFilter === 'all' || (contactTempFilter === 'optin' ? c.opt_in_ativo !== false : contactTempFilter === 'nooptin' ? c.opt_in_ativo === false : contactTempFilter === 'erro' ? !!c.ultimo_erro_envio : true);
                            return matchSearch && matchOptIn;
                          });
                          return `${filtered.length} contatos`;
                        })()}
                      </h4>
                      <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                        Página {contactsPage}/{Math.ceil(contacts.filter(c => {
                          const search = contactSearchTerm.toLowerCase();
                          const matchSearch = !search || c.nome?.toLowerCase().includes(search) || c.telefone?.includes(search);
                          const matchOptIn = contactTempFilter === 'all' || (contactTempFilter === 'optin' ? c.opt_in_ativo !== false : contactTempFilter === 'nooptin' ? c.opt_in_ativo === false : contactTempFilter === 'erro' ? !!c.ultimo_erro_envio : true);
                          return matchSearch && matchOptIn;
                        }).length / contactsPerPage) || 1}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {contacts.filter(c => {
                        const search = contactSearchTerm.toLowerCase();
                        const matchSearch = !search || c.nome?.toLowerCase().includes(search) || c.telefone?.includes(search);
                        const matchOptIn = contactTempFilter === 'all' || (contactTempFilter === 'optin' ? c.opt_in_ativo !== false : contactTempFilter === 'nooptin' ? c.opt_in_ativo === false : contactTempFilter === 'erro' ? !!c.ultimo_erro_envio : true);
                        return matchSearch && matchOptIn;
                      }).sort((a, b) => {
                        if (contactTempFilter === 'erro') return 0;
                        if (a.ultimo_erro_envio && !b.ultimo_erro_envio) return 1;
                        if (!a.ultimo_erro_envio && b.ultimo_erro_envio) return -1;
                        return (a.nome || '').localeCompare(b.nome || '');
                      }).slice((contactsPage - 1) * contactsPerPage, contactsPage * contactsPerPage).map((contact) => (
                        <div
                          key={contact.id}
                          className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                            darkMode
                              ? "border-gray-700 bg-zinc-700 hover:bg-zinc-600"
                              : "border-gray-200 bg-white hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                                  <Phone className="h-5 w-5 text-green-600" />
                                </div>
                              </div>
                              <div>
                                <p className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                                  {contact.nome}
                                </p>
                                <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                                  {contact.telefone}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                contact.ultimo_erro_envio
                                  ? 'bg-red-100 text-red-700'
                                  : contact.opt_in_ativo !== false
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`} title={contact.ultimo_erro_envio ? 'Erro no último envio — número pode ser inválido' : contact.opt_in_ativo !== false ? 'Opt-in ativo' : 'Sem opt-in - não receberá campanhas'}>
                                {contact.ultimo_erro_envio ? '❌' : contact.opt_in_ativo !== false ? '✅' : '⚠️'}
                              </span>
                              <button
                                onClick={() => handleRemoveContact(contact.id)}
                                className="text-red-600 hover:text-red-900 p-1"
                                title="Remover do grupo"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Paginação */}
                    {contacts.length > contactsPerPage && (
                      <div className="flex items-center justify-center gap-2 mt-4">
                        <button
                          onClick={() => setContactsPage(p => Math.max(1, p - 1))}
                          disabled={contactsPage === 1}
                          className={`px-3 py-1.5 rounded text-sm font-medium disabled:opacity-40 ${
                            darkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          ← Anterior
                        </button>
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {contactsPage} / {Math.ceil(contacts.length / contactsPerPage)}
                        </span>
                        <button
                          onClick={() => setContactsPage(p => Math.min(Math.ceil(contacts.length / contactsPerPage), p + 1))}
                          disabled={contactsPage >= Math.ceil(contacts.length / contactsPerPage)}
                          className={`px-3 py-1.5 rounded text-sm font-medium disabled:opacity-40 ${
                            darkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Próxima →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "messages" && false && (
              <div className="space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className={`mt-2 text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                      Nenhuma mensagem
                    </h3>
                    <p className={`mt-1 text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                      Nenhuma mensagem foi enviada para este grupo.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Array.isArray(messages) && messages.map((message) => (
                      <div
                        key={message.id}
                        className={`border rounded-lg p-4 ${
                          darkMode
                            ? "border-gray-700 bg-zinc-700"
                            : "border-gray-200 bg-white"
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center space-x-2">
                            <MessageSquare className="h-4 w-4 text-green-600" />
                            <span className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                              {message.tipo || "Mensagem"}
                            </span>
                          </div>
                          <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                            {formatarDataHora(message.created_at)}
                          </span>
                        </div>
                        <p className={`text-sm mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                          {message.text}
                        </p>
                        <div className={`flex justify-between items-center text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                          <span>
                            Para: {message.destinatarios || contacts.length} contato(s)
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full ${
                              message.status === "enviada"
                                ? "bg-green-100 text-green-800"
                                : message.status === "pendente"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {message.status || "Enviada"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Add Contact Modal */}
        {showAddModal && (
          <div className="modal-overlay">
            <div className={`modal-container ${darkMode ? "bg-zinc-800" : "bg-white"}`} style={{ maxWidth: '1200px', width: '90%' }}>
              <div className="mt-3">
                <div className="modal-header">
                  <h3 className={`modal-title ${darkMode ? "text-white" : "text-gray-900"}`}>
                    Adicionar Contatos ao Grupo: {group?.nome}
                  </h3>
                  <div className={`text-sm ${darkMode ? "text-white" : "text-gray-600"}`}>
                    {selectedContacts.length} selecionado(s)
                  </div>
                </div>

                {/* Search contacts */}
                <div className="modal-body">
                  <div className="grid grid-cols-3 gap-6">
                    {/* Lista de contatos disponíveis - 2 colunas */}
                    <div className="col-span-2 space-y-4">
                      <div className="mb-4">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Buscar contatos..."
                            className={`mt-1 block w-full rounded-md border py-2 px-3 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-nomoz-orange ${
                              darkMode
                                ? "border-gray-600 bg-zinc-700 text-white"
                                : "border-gray-300 bg-white text-gray-900"
                            }`}
                            value={contactSearch}
                            onChange={(e) => setContactSearch(e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Selection controls */}
                      <div className={`flex justify-between items-center p-2 rounded ${darkMode ? "bg-zinc-700" : "bg-gray-50"}`}>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              const availableIds = getAvailableContacts().map((c) => c.id);
                              setSelectedContacts(availableIds);
                            }}
                            className="text-sm text-green-500 hover:text-green-800"
                          >
                            Selecionar Todos
                          </button>
                          <span className={darkMode ? "text-white" : "text-gray-500"}>|</span>
                          <button
                            onClick={() => setSelectedContacts([])}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Limpar
                          </button>
                        </div>
                        <div className={`text-sm font-medium ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                          {getAvailableContacts().length} disponíveis
                        </div>
                      </div>

                      {/* Contacts grid */}
                      <div className="max-h-96 overflow-y-auto">
                        {getAvailableContacts().filter(c => !selectedContacts.includes(c.id)).length === 0 ? (
                          <div className={`p-8 text-center ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                            <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>Nenhum contato disponível</p>
                            <p className="text-xs mt-1">
                              {selectedContacts.length > 0 
                                ? "Todos os contatos disponíveis foram selecionados" 
                                : "Todos os contatos já estão neste grupo"}
                            </p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {getAvailableContacts().map((contact) => (
                          <div
                            key={contact.id}
                            onClick={() => {
                              if (contact.alreadyInGroup) return;
                              setSelectedContacts((prev) =>
                                prev.includes(contact.id)
                                  ? prev.filter((id) => id !== contact.id)
                                  : [...prev, contact.id]
                              );
                            }}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              contact.alreadyInGroup
                                ? darkMode
                                  ? "border-gray-600 bg-gray-800 opacity-60 cursor-not-allowed"
                                  : "border-gray-300 bg-gray-100 opacity-60 cursor-not-allowed"
                                : selectedContacts.includes(contact.id)
                                ? darkMode 
                                  ? "border-nomoz-orange bg-green-900/30 shadow-lg cursor-pointer" 
                                  : "border-nomoz-orange bg-green-50 shadow-lg cursor-pointer"
                                : darkMode
                                  ? "border-gray-600 bg-zinc-700 hover:border-green-400 hover:shadow-md cursor-pointer"
                                  : "border-gray-200 bg-white hover:border-green-300 hover:shadow-md cursor-pointer"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                  {contact.nome?.charAt(0)?.toUpperCase() || "?"}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium truncate ${darkMode ? "text-white" : "text-gray-900"}`}>
                                    {contact.nome}
                                  </p>
                                  <p className={`text-xs truncate ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                                    {contact.telefone}
                                    {contact.alreadyInGroup && (
                                      <span className={`ml-2 text-[10px] ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                                        • Já no grupo
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0">
                                {contact.alreadyInGroup ? (
                                  <div className={`h-6 w-6 rounded-full flex items-center justify-center ${darkMode ? "bg-gray-600" : "bg-gray-400"}`}>
                                    <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                ) : selectedContacts.includes(contact.id) ? (
                                  <div className="h-6 w-6 rounded-full bg-green-500 flex items-center justify-center">
                                    <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                ) : (
                                  <div className={`h-6 w-6 rounded-full border-2 ${darkMode ? "border-gray-500" : "border-gray-300"}`} />
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                    </div>

                    {/* Sidebar com contatos selecionados - 1 coluna */}
                    <div className={`col-span-1 border-l pl-4 ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
                      <div className="sticky top-0">
                        <h4 className={`text-sm font-semibold mb-3 ${darkMode ? "text-white" : "text-gray-900"}`}>
                          Selecionados ({selectedContacts.length})
                        </h4>
                        <div className="max-h-96 overflow-y-auto space-y-2">
                          {selectedContacts.filter(contactId => {
                            const contact = allContacts.find(c => c.id === contactId);
                            const groupContactIds = contacts.map((c) => c.contato?.id || c.contato_id);
                            return contact && !groupContactIds.includes(contact.id);
                          }).length === 0 ? (
                            <p className={`text-xs text-center py-8 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                              Nenhum contato selecionado
                            </p>
                          ) : (
                            selectedContacts.map((contactId) => {
                              const contact = allContacts.find(c => c.id === contactId);
                              const groupContactIds = contacts.map((c) => c.contato?.id || c.contato_id);
                              if (!contact || groupContactIds.includes(contact.id)) return null;
                              return (
                                <div
                                  key={contactId}
                                  className={`p-2 rounded-lg border flex items-center gap-2 ${
                                    darkMode 
                                      ? "bg-green-900/20 border-nomoz-orange/50" 
                                      : "bg-green-50 border-green-200"
                                  }`}
                                >
                                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                    {contact.nome?.charAt(0)?.toUpperCase() || "?"}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-xs font-medium truncate ${darkMode ? "text-white" : "text-gray-900"}`}>
                                      {contact.nome}
                                    </p>
                                    <p className={`text-[10px] truncate ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                                      {contact.telefone}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => {
                                      setSelectedContacts(prev => prev.filter(id => id !== contactId));
                                    }}
                                    className="flex-shrink-0 text-red-500 hover:text-red-700"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Modal actions */}
                  <div className="flex justify-between pt-4">
                    <button
                      onClick={() => {
                        setShowAddModal(false);
                        setSelectedContacts([]);
                        setContactSearch("");
                      }}
                      className={`modal-button px-4 py-2 border rounded-md text-sm font-medium ${
                        darkMode
                          ? "border-gray-600 text-white hover:bg-zinc-600"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={async () => {
                        if (selectedContacts.length === 0) {
                          toast.error("Selecione pelo menos um contato");
                          return;
                        }

                        if (!id) {
                          toast.error('Nenhum grupo selecionado');
                          return;
                        }

                        try {
                          const contactsData = selectedContacts.map((contactId) => ({
                            grupo_id: id,
                            contato_id: contactId,
                            cliente_id: user.cliente_id,
                            status: true,
                          }));

                          await groupsAPI.addContact(contactsData);
                          toast.success(
                            `${selectedContacts.length} contato(s) adicionado(s) ao grupo!`
                          );
                          setSelectedContacts([]);
                          setContactSearch("");
                          await loadGroupDetails();
                          setShowAddModal(false);
                        } catch (error) {
                          console.error("Erro ao adicionar contatos:", error);
                          
                          const errorMessage = error.response?.data?.message || 
                                              error.message || 
                                              "Erro ao adicionar contatos";
                          
                          if (error.response?.status === 409) {
                            toast.warning(errorMessage, { autoClose: 7000 });
                          } else {
                            toast.error(errorMessage);
                          }
                        }
                      }}
                      disabled={selectedContacts.length === 0}
                      className="gradient-button px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Adicionar{" "}
                      {selectedContacts.length > 0 ? `(${selectedContacts.length})` : ""}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CSV Import Modal */}
        {showCSVModal && (
          <div className="modal-overlay">
            <div className={`modal-container max-w-2xl ${darkMode ? "bg-zinc-800" : "bg-white"}`}>
              <div className="modal-header">
                <h3 className={`modal-title ${darkMode ? "text-white" : "text-gray-900"}`}>
                  Importar Contatos via CSV
                </h3>
                <button
                  onClick={() => {
                    setShowCSVModal(false);
                    setCsvFile(null);
                  }}
                  className={`modal-close-button ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="modal-body space-y-4">
                <div className={`p-4 rounded-lg ${darkMode ? "bg-yellow-900/20 border-yellow-800" : "bg-yellow-50 border-yellow-200"} border`}>
                  <h4 className={`text-sm font-medium ${darkMode ? "text-yellow-400" : "text-yellow-800"}`}>
                    📝 Formato do CSV
                  </h4>
                  <p className={`text-xs mt-1 ${darkMode ? "text-yellow-300" : "text-yellow-700"}`}>
                    Use as colunas: <code>nome,telefone</code> ou <code>name,phone</code>
                  </p>
                  <div className={`mt-2 text-xs ${darkMode ? "text-yellow-300" : "text-yellow-700"}`}>
                    <strong>Exemplo:</strong><br/>
                    <code>nome,telefone<br/>João Silva,11999887766<br/>Maria Santos,11888776655</code>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
                    Arquivo CSV
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className={`block w-full text-sm rounded-md border py-2 px-3 ${
                      darkMode
                        ? "border-gray-600 bg-zinc-700 text-white file:bg-zinc-600 file:text-white"
                        : "border-gray-300 bg-white text-gray-900 file:bg-gray-50 file:text-gray-700"
                    } file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium hover:file:bg-gray-100`}
                  />
                </div>

                <div className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                  ℹ️ O sistema irá:
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Procurar contatos existentes por telefone</li>
                    <li>Criar novos contatos se não existirem</li>
                    <li>Adicionar todos os contatos ao grupo</li>
                  </ul>
                </div>
              </div>

              {/* Consentimento */}
              <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer ${darkMode ? 'border-gray-600 bg-zinc-700' : 'border-gray-200 bg-gray-50'}`}>
                <input type="checkbox" checked={consentimentoDeclarado} onChange={e => setConsentimentoDeclarado(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded text-nomoz-orange focus:ring-nomoz-orange" />
                <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Declaro que possuo consentimento prévio de todos os contatos para receber comunicações.
                </span>
              </label>

              <div className="modal-footer">
                <button
                  onClick={() => {
                    setShowCSVModal(false);
                    setCsvFile(null);
                  }}
                  className={`modal-button ${
                    darkMode
                      ? "border-gray-600 text-white hover:bg-zinc-600"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    if (!csvFile) {
                      toast.error('Selecione um arquivo CSV');
                      return;
                    }

                    setImporting(true);
                    try {
                      const text = await csvFile.text();
                      const lines = text.split('\n').filter(line => line.trim());

                      if (lines.length < 2) {
                        toast.error("Arquivo CSV deve ter pelo menos um cabeçalho e uma linha de dados");
                        setImporting(false);
                        return;
                      }

                      // Detectar delimitador
                      const headerLine = lines[0];
                      const commaSplit = headerLine.split(',');
                      const semicolonSplit = headerLine.split(';');
                      const delimiter = (semicolonSplit.length > commaSplit.length) ? ';' : ',';

                      const headers = headerLine.split(delimiter).map(h => h.trim().toLowerCase());
                      const nomeIndex = headers.findIndex(h => h.includes('nome'));
                      const telefoneIndex = headers.findIndex(h => h.includes('telefone') || h.includes('phone'));

                      if (nomeIndex === -1 || telefoneIndex === -1) {
                        toast.error("CSV deve conter colunas 'nome' e 'telefone'");
                        setImporting(false);
                        return;
                      }

                      // Validar algumas linhas
                      const erros = [];
                      const telefonesVistos = new Set();
                      for (let i = 1; i < Math.min(lines.length, 6); i++) {
                        const rawValues = lines[i].split(delimiter);
                        const values = rawValues.map(v => v.trim().replace(/"/g, ''));

                        if (values.length >= Math.max(nomeIndex + 1, telefoneIndex + 1)) {
                          const nome = (values[nomeIndex] || '').trim();
                          const telefoneBruto = (values[telefoneIndex] || '').trim();
                          const telefone = telefoneBruto.replace(/\D/g, '');

                          if (!nome) {
                            erros.push(`Linha ${i + 1}: nome vazio.`);
                          }
                          if (!/^(?!0+$)\d{10,13}$/.test(telefone)) {
                            erros.push(`Linha ${i + 1}: telefone inválido (${telefoneBruto}).`);
                          }
                          if (telefonesVistos.has(telefone)) {
                            erros.push(`Linha ${i + 1}: telefone duplicado (${telefone}).`);
                          }
                          telefonesVistos.add(telefone);
                        }
                      }

                      if (erros.length) {
                        toast.error(`Erros no CSV:\n${erros.join('\n')}`);
                        setImporting(false);
                        return;
                      }

                      const response = await groupsAPI.importCSV(id, {
                        csvData: text,
                        cliente_id: user.cliente_id,
                        usuario_id: user.id,
                        consentimento_declarado: consentimentoDeclarado,
                      });

                      if (response.data.success) {
                        const { results } = response.data;
                        toast.success(
                          `Importação concluída! ${results.adicionados_ao_grupo} contatos adicionados ao grupo.`
                        );
                        
                        if (results.erros.length > 0) {
                          console.warn('Erros na importação:', results.erros);
                        }
                        
                        setShowCSVModal(false);
                        setCsvFile(null);
                        loadGroupDetails();
                      } else {
                        toast.error('Erro na importação');
                      }
                    } catch (error) {
                      console.error('Erro ao importar CSV:', error);
                      toast.error(error.response?.data?.message || 'Erro ao importar CSV');
                    } finally {
                      setImporting(false);
                    }
                  }}
                  disabled={!csvFile || importing}
                  className="gradient-button px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importing ? 'Importando...' : 'Importar Contatos'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && (
          <div className="modal-overlay" onClick={() => setDeleteModal(false)}>
            <div
              className={`modal-container max-w-md ${darkMode ? "bg-zinc-800" : "bg-white"}`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="modal-header">
                <h3 className={`modal-title text-red-400 ${darkMode ? "text-red-400" : "text-red-600"}`}>
                  Confirmar Exclusão
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteModal(false);
                  }}
                  className={`modal-close-button ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  ×
                </button>
              </div>

              {/* Body */}
              <div className="modal-body">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-10 h-10 text-red-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className={`text-lg font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                      Tem certeza que deseja excluir este contato?
                    </h4>
                    <p className={`text-sm mt-1 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                      Esta ação não pode ser desfeita.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => {
                    setDeleteModal(false);
                  }}
                  className={`modal-button modal-button-secondary ${
                    darkMode
                      ? "border-gray-600 text-white hover:bg-zinc-600"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleDeleteGroup}
                  className="modal-button bg-red-600 hover:bg-red-700 text-white"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GroupDetails;